import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, MenuItem, Button,
    FormControl, Select, FormHelperText, CircularProgress, Snackbar, Alert
} from "@mui/material";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { isBefore, startOfDay, format } from 'date-fns';
import { getData, postData } from '../../services/axiosInstance';
import { API_ENDPOINTS } from '../../services/endpoints';

function LeaveApplicationForm({ onClose }) {
    const today = startOfDay(new Date());
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const reportMgrId = userData?.report_manager_id;
    const organizationId = userData?.organization_id;
    const locationId = userData?.location_id;
    const [formData, setFormData] = useState({
        leave_type_id: '',
        startDate: today,
        endDate: today,
        reason: ''
    });
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [fetchingTypes, setFetchingTypes] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

    useEffect(() => {
        const getLeaveTypes = async () => {
            if (!organizationId) return;
            setFetchingTypes(true);
            try {
                const response = await getData(API_ENDPOINTS.fetchLeaveCountsForOrganization(organizationId));
                if (response?.message?.leaveTypes) {
                    setLeaveTypes(response.message.leaveTypes);
                }
            } catch (error) {
                return null;
            } finally {
                setFetchingTypes(false);
            }
        };
        getLeaveTypes();
    }, [organizationId]);

    const validate = () => {
        let tempErrors = {};
        if (!formData.leave_type_id) tempErrors.leave_type_id = "Please select a leave type";
        if (!formData.startDate) tempErrors.startDate = "Start date is required";
        if (!formData.endDate) tempErrors.endDate = "End date is required";
        if (!formData.reason.trim()) tempErrors.reason = "Please provide a reason";

        if (formData.startDate && formData.endDate) {
            if (isBefore(startOfDay(formData.endDate), startOfDay(formData.startDate))) {
                tempErrors.endDate = "End date cannot be earlier than start date";
            }
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: '' });
    };

    const handleDateChange = (name, date) => {
        let newFormData = { ...formData, [name]: date };
        if (name === 'startDate' && formData.endDate && isBefore(startOfDay(formData.endDate), startOfDay(date))) {
            newFormData.endDate = date;
        }

        setFormData(newFormData);
        if (errors[name]) setErrors({ ...errors, [name]: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setLoading(true);

            const payload = {
                user_id: userId,
                start_date: format(formData.startDate, 'yyyy-MM-dd'),
                end_date: format(formData.endDate, 'yyyy-MM-dd'),
                organization_id: organizationId,
                location_id: locationId,
                leave_type_id: formData.leave_type_id,
                report_manager_id: reportMgrId || null,
                leave_duration_type: "FULL_DAY",
                reason: formData.reason
            };

            try {
                const response = await postData(API_ENDPOINTS.createLeaveRequest, payload);
                if (response.status === 'SUCCESS') {
                    setSnackbar({ open: true, message: "Leave Request Submitted Successfully!", severity: 'success' });
                    setTimeout(() => {
                        if (onClose) {
                            onClose();
                        }
                    }, 1000);
                } else {
                    setSnackbar({
                        open: true,
                        message: response.message || "Something went wrong.",
                        severity: 'error'
                    });
                }
            } catch (error) {
                setSnackbar({ open: true, message: "Failed to submit request. Please try again.", severity: 'error' });
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Leave Type</Typography>
                        <FormControl fullWidth error={!!errors.leave_type_id} size="small">
                            <Select
                                name="leave_type_id"
                                value={formData.leave_type_id}
                                onChange={handleChange}
                                displayEmpty
                                disabled={fetchingTypes}
                                sx={{ borderRadius: 2 }}
                                startAdornment={fetchingTypes && <CircularProgress size={20} sx={{ mr: 1 }} />}
                            >
                                <MenuItem value="" disabled>
                                    {fetchingTypes ? "Loading..." : "Select Leave Type"}
                                </MenuItem>
                                {leaveTypes.map((type) => (
                                    <MenuItem key={type.id} value={type.id}>{type.leave_type}</MenuItem>
                                ))}
                            </Select>
                            {errors.leave_type_id && <FormHelperText>{errors.leave_type_id}</FormHelperText>}
                        </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Start Date</Typography>
                        <DatePicker
                            sx={{ width: '100%' }}
                            value={formData.startDate}
                            onChange={(date) => handleDateChange('startDate', date)}
                            minDate={today}
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    error: !!errors.startDate,
                                    helperText: errors.startDate,
                                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>End Date</Typography>
                        <DatePicker
                            sx={{ width: '100%' }}
                            value={formData.endDate}
                            onChange={(date) => handleDateChange('endDate', date)}
                            minDate={formData.startDate || today} // Prevents picking date before start date
                            format="dd/MM/yyyy"
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    error: !!errors.endDate,
                                    helperText: errors.endDate,
                                    sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                                }
                            }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>Reason for Leave</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            name="reason"
                            value={formData.reason}
                            onChange={handleChange}
                            error={!!errors.reason}
                            helperText={errors.reason}
                            placeholder="Describe your reason here..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || fetchingTypes}
                            sx={{
                                textTransform: 'none', px: 4, borderRadius: 2,
                                backgroundColor: '#6366f1',
                                '&:hover': { backgroundColor: '#4338ca' }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Request'}
                        </Button>
                    </Grid>
                </Grid>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
}

export default LeaveApplicationForm;